import { z } from 'zod';
import {
  messageAttachment,
  MAX_ATTACHMENTS_PER_MESSAGE,
  clientOptionalString,
  clientOptionalBoolean,
} from './common';

// ---------------------------------------------------------------------------
// App channel — POST /channels/app/message
//
// The app channel is the one `/channels/*` ingress whose payload is FIRST-PARTY:
// it is authMiddleware-gated and its body is authored by our own web/mobile
// clients (`@wayai/state` message-send). The provider webhooks next to it are
// contract-exempt because their bodies are vendor-defined; this one is ours, so
// the "new endpoints must have contracts first" rule applies. It previously
// parsed with a raw `c.req.json<AppMessageRequest>()` cast — typed but never
// validated, which reads as safe and isn't.
//
// `sender_type` is the security-critical field. It is stored verbatim on the
// message row, and `buildLlmMessageHistory` replays a row whose sender is
// 'agent' as `role: 'assistant'`. Left unbounded, an authenticated user could
// POST `sender_type: 'agent'` into their own conversation and have their text
// attributed to the AI — a durable prompt-injection primitive ("the assistant
// previously said X", replayed on every later turn) and a forged transcript the
// support team reads as an AI message (gh #3298). The enum below is the
// whitelist: 'user' and 'team' are the only senders a client may CLAIM, and
// 'team' is separately AUTHORIZED downstream by `validateAppTeamSendContext`.
// 'agent' | 'system' | 'tool' rows are server-authored only — no client may mint
// one on any route (every other `insertMessage` caller hardcodes its sender).
//
// Deliberately NOT `.strict()`: zod strips unknown keys, so mobile builds already
// in the wild that send fields this route never reads (`hub_team_user_id`,
// `hub_user_id`, `user_identity_id`) keep working exactly as they do today. A
// strict schema would 400 them.
// ---------------------------------------------------------------------------

/**
 * `clientOptional*` normalize an explicit wire `null` to undefined (see contracts/common).
 * Null is reachable here today: a hub's `app_channel_id` is `string | null`, and the
 * task-hub send path deliberately forwards a falsy one (`messageStore` only hard-fails on
 * a missing channel for non-task navs), so a first task message posts `channel_id: null`.
 * Every consumer in the route already collapses null and absent to the same thing
 * (`body.channel_id || null`), so normalizing beats 400ing a send that worked before this
 * contract existed.
 */

/** The only senders a client may claim. See the header. */
export const appMessageSenderType = z.enum(['user', 'team']);
export type AppMessageSenderType = z.infer<typeof appMessageSenderType>;

/**
 * 'assistant' is a legacy alias for 'agent'. Normalized HERE rather than in the route so
 * the handler only ever sees the three real receiver types — that is what lets the
 * downstream value stay a plain `const` instead of an `as` cast, which is the same
 * typed-but-unchecked pattern this contract exists to remove.
 */
export const appMessageReceiverType = z
  .enum(['user', 'team', 'agent', 'assistant'])
  .transform((v) => (v === 'assistant' ? ('agent' as const) : v));
export type AppMessageReceiverType = z.infer<typeof appMessageReceiverType>;

/**
 * Streaming app-channel responses cannot carry the normal JSON result body, so
 * the allocated conversation id rides beside the SSE body in this header. Web
 * and mobile use it to atomically promote an explicit task-start placeholder.
 */
export const APP_MESSAGE_CONVERSATION_ID_HEADER = 'X-WayAI-Conversation-Id';

// ---------------------------------------------------------------------------
// API channel — POST /channels/api/:hub_id/:channel_id/message
//
// Machine-to-machine ingress: an external system POSTs a message and receives the
// reply asynchronously via signed webhook. authMiddleware-gated + hub-admin-grade,
// so this is a first-party-authored payload — contract-first applies (NOT the
// vendor-webhook exemption). Like the app channel, deliberately NOT `.strict()`.
//
// Conversation selectors: `external_ref` (caller correlation id — start-or-continue,
// task hubs) and `conversation_id` (continue a specific WayAI conversation) are
// mutually exclusive; supplying both is ambiguous. Hub-type semantics (task vs chat,
// chat-requires-user, service-principal) are enforced in the route with 422s — the
// contract only validates shape.
// ---------------------------------------------------------------------------

export const apiChannelMessage = z.object({
  text: clientOptionalString,
  attachments: z.array(messageAttachment).max(MAX_ATTACHMENTS_PER_MESSAGE).nullish()
    .transform((v) => v ?? undefined),
}).refine((m) => (m.text && m.text.length > 0) || (m.attachments && m.attachments.length > 0), {
  message: 'message must have text or at least one attachment',
});

export const apiChannelMessageBody = z.object({
  // Correlation id (task hubs): new → new conversation, repeat → continue. min 1 so an
  // empty string can't masquerade as a ref. Mutually exclusive with conversation_id.
  external_ref: z.string().min(1).max(256).optional(),
  // Continue a specific WayAI conversation (authorized against the hub/channel + owner).
  // UUID-validated here (unlike the app channel, whose client we control): allocation
  // only treats a UUID-shaped id as a provided conversation, so a malformed id would
  // otherwise skip the owner guard and silently MINT a new conversation instead of
  // rejecting. A format 400 is shape-only — not an existence oracle (a valid-but-unknown
  // UUID still collapses to the opaque authorization error downstream).
  conversation_id: z.string().uuid().optional(),
  // On-behalf-of end-user identity. Absent → per-connection service principal (task-hub
  // only; chat hub rejects with 422 in the route).
  user: z.object({
    id: z.string().min(1),
    email: clientOptionalString,
    name: clientOptionalString,
  }).optional(),
  message: apiChannelMessage,
  // Idempotency key: maps to channel_message_sid, deduped within one conversation
  // (ConversationDO.storeMessage). A retried POST resolves to the same conversation (a chat
  // hub's user, the external_ref, the conversation_id, or on a task hub with neither, this key
  // scoped by the sender), so it echoes the original message_id and skips billing / turn
  // dispatch. A failed store answers a retryable 503 only when this key is set; without it a
  // retry could store the message twice, so it answers 400.
  client_message_id: z.string().min(1).max(256).optional(),
}).refine((b) => !(b.external_ref && b.conversation_id), {
  message: 'provide external_ref or conversation_id, not both',
  path: ['external_ref'],
});
export type ApiChannelMessageBody = z.infer<typeof apiChannelMessageBody>;

/** 202 response — processing is async; the reply arrives via webhook. */
export interface ApiChannelMessageResponse {
  conversation_id: string;
  message_id: string;
}

export const appMessageBody = z.object({
  // Message content — three historical aliases. All optional: an attachment-only
  // or voice-only message carries no text.
  content: clientOptionalString,
  message_text: clientOptionalString,
  message_body: clientOptionalString,

  // Conversation context. Ids are plain strings rather than `.uuid()` on purpose:
  // they are AUTHORIZED downstream, which is the stronger guarantee — a well-formed
  // uuid belonging to another tenant must fail just as hard as junk. `hub_id` is
  // gated by `assertHubEndUserAccess` (gh #3286) and `conversation_id` by HubDO's
  // conversation_index owner check (gh #2781, #3281). The route's own falsy guards
  // ('hub_id is required') still reject an empty one.
  hub_id: clientOptionalString,
  channel_id: clientOptionalString,
  conversation_id: clientOptionalString,
  hub_type: z.enum(['chat', 'task']).nullish().transform((v) => v ?? undefined),
  // Stable idempotency key for one explicit task-hub launcher. HubDO scopes it
  // to the authenticated user and resolves retries/tabs to one conversation.
  task_start_id: z.string().uuid().nullish().transform((v) => v ?? undefined),

  // Routing.
  sender_type: appMessageSenderType.nullish().transform((v) => v ?? undefined),
  receiver_type: appMessageReceiverType.nullish().transform((v) => v ?? undefined),
  // Only meaningful on a team send, where `validateAppTeamSendContext` proves the
  // caller may send to this receiver. The end-user branch ignores it — see the
  // receiver derivation in the route.
  user_receiver_id: clientOptionalString,

  // ── Internal consult (consultant-agents PR 2 — H1.0) ──────────────────────
  // A team member handling a customer conversation can tag a `consultant`-role
  // agent for a PRIVATE, team-only exchange that never reaches the customer. A
  // consult send is a team send with `receiver_type: 'agent'` targeting the
  // consultant; the still-required `user_receiver_id` identifies the customer whose
  // conversation this is (and is authorized by `validateAppTeamSendContext`, exactly
  // as a team→user send is). The consultant is resolved from `agent_receiver_id`
  // when supplied, else from an `@mention` parsed out of the message text against
  // the hub's consultant agents. `consult_thread_id` continues an existing thread;
  // absent, the route creates/reuses the single implicit thread. These fields are
  // inert on every non-consult send (end-user send, team→user send).
  agent_receiver_id: clientOptionalString,
  consult_thread_id: clientOptionalString,

  is_first_message: clientOptionalBoolean,
  end_conversation: clientOptionalBoolean,

  // Pre-generated by the client so the optimistic bubble and the stored row share
  // a key (transcription live-updates land on the same row instead of duplicating).
  message_id: clientOptionalString,

  attachments: z.array(messageAttachment).max(MAX_ATTACHMENTS_PER_MESSAGE).nullish()
    .transform((v) => v ?? undefined),
  // Base64-encoded audio for STT. Size-capped in the route (the cap is a byte
  // budget tied to the /channels/* bodyLimit, not a shape rule).
  audio_file: clientOptionalString,
});
export type AppMessageBody = z.infer<typeof appMessageBody>;

/**
 * JSON response for POST /channels/app/message. Streaming responses carry SSE
 * instead; their allocated conversation id rides in
 * APP_MESSAGE_CONVERSATION_ID_HEADER.
 */
export const appMessageResponse = z.object({
  success: z.boolean(),
  message: z.string(),
  result: z.object({
    conversation_id: z.string().optional(),
    message_id: z.string().optional(),
    status: z.string().optional(),
    files: z.array(z.record(z.string(), z.unknown())).optional(),
    // True when this request reused a task-start/message id already accepted by
    // the server. Clients must replace their optimistic payload with a fresh
    // authoritative read because another tab may have won with different text.
    deduped: z.boolean().optional(),
  }).optional(),
});
export type AppMessageResponse = z.infer<typeof appMessageResponse>;
